import { CollectionPreferencesProps } from "@amzn/awsui-components-react";
import { PostOrders_AllCommandInput, PostOrdersAllResponseContent, Order } from "@amzn/ito-client";
import { CTI_ERROR } from "common/constants";
import { ErrorProps } from "interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ITOOrdersService } from "services/orders/ito-orders-service";

/**
 * Unique identifier property name for orders
 */
export const orderUniqueIdentifier: string = "orderId";

const AVAILABLE_PAGE_SIZES = [5, 10, 15, 20, 30, 50];
export const DEFAULT_PAGE_SIZE = 30;
const DEFAULT_PAGE = 0;

/**
 * Props interface for the useGetCustomQueryOrders hook
 *
 * @interface
 * @property {Record<string, any>} [query] - The query object for filtering orders
 * @property {boolean} [isFiltersLoading] - Flag determining if the filters are already loaded to fetch in ITO
 */
export interface useGetCustomQueryOrdersProps {
    query?: Record<string, any>;
    isFiltersLoading?: boolean;
    pageSize?: number;
}

/**
 * Custom hook for fetching and managing orders with custom query structure and pagination
 *
 * @param {useGetCustomQueryOrdersProps} props - Configuration object for the hook
 * @return {object} - Object containing pagination controls and order data
 */
const useGetCustomQueryOrders = ({
    query,
    isFiltersLoading,
    pageSize: parameterPageSize,
}: useGetCustomQueryOrdersProps) => {
    const [nextToken, setNextToken] = useState<number>(DEFAULT_PAGE);
    const [pageSize, setPageSize] = useState<number>(parameterPageSize ?? DEFAULT_PAGE_SIZE);
    const [totalResults, setTotalResults] = useState<number>(0);

    /**
     * Handle page navigation by converting 1-based page number to 0-based token
     *
     * @param {number} page - Target page number (1-based)
     * @return {void} - No return value
     */
    const onChangePage = (page: number) => {
        setNextToken(page - 1);
    };

    /**
     * Get available page size options for the select component
     *
     * @return {CollectionPreferencesProps.PageSizeOption[]} - Array of page size options
     */
    const getAvailablePageSizes = (): CollectionPreferencesProps.PageSizeOption[] => {
        return AVAILABLE_PAGE_SIZES.map((size) => ({
            label: size.toString(),
            value: size,
        }));
    };

    /**
     * Update the current page size and reset to first page
     *
     * @param {number} size - New page size value
     * @return {void} - No return value
     */
    const updateCurrentPageSize = (size: number) => {
        setPageSize(size);
    };

    const [error, setError] = useState<ErrorProps>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
    const [orders, setOrders] = useState<Order[] | undefined>([]);

    const itoService = new ITOOrdersService();

    const savedQuery = useMemo(() => {
        return query ? JSON.stringify(query) : null;
    }, [query]);

    const fetchOrders = useCallback(
        (nextToken: string) => {
            setIsLoading(true);

            const requestInput: PostOrders_AllCommandInput = {
                nextToken,
                pageSize,
                query,
            };

            itoService
                .getCustomQueryOrders(requestInput)
                .then((response: PostOrdersAllResponseContent) => {
                    setOrders(response.orders);
                    setTotalResults(response.totalResults ?? 0);
                })
                .catch((error) => {
                    setError(CTI_ERROR);
                })
                .finally(() => {
                    setIsLoading(false);
                    setIsPageLoading(false);
                });
        },
        [itoService, pageSize, query, setOrders, setTotalResults, setError, setIsLoading, setIsPageLoading],
    );

    /**
     * This use effect handles changes which require a page update and to set the pagination spinner
     */
    useEffect(() => {
        if (isFiltersLoading) {
            return;
        }

        setIsPageLoading(true);
        setNextToken(DEFAULT_PAGE);
        fetchOrders(DEFAULT_PAGE.toString());
    }, [savedQuery, pageSize]);

    /**
     * This use effect handles pages changes that don't require the pagination spinner
     */
    useEffect(() => {
        if (isFiltersLoading || isPageLoading) {
            return;
        }

        fetchOrders(nextToken.toString());
    }, [nextToken]);

    return {
        pagination: {
            currentPage: nextToken + 1,
            pageSize,
            defaultPageSize: DEFAULT_PAGE_SIZE,
            onChange: onChangePage,
            getPageSizeOptions: getAvailablePageSizes,
            onPageSizeChange: updateCurrentPageSize,
            totalPages: Math.ceil(totalResults / pageSize),
        },
        data: {
            fetch: fetchOrders,
            orders,
            trackBy: orderUniqueIdentifier,
            error,
            isLoading,
            isPageLoading,
            totalOrders: totalResults,
        },
    };
};

export default useGetCustomQueryOrders;
